// modal.js
import { gsap } from "gsap";

import { initializeSwiper } from "./swiper";

// constants
const modal = document.querySelector(".modal");
const modalContentWrapper = document.querySelector(".modal-content-wrapper");
const modalClose = document.querySelector(".modal-close");

// Animation timelines
const modalTimeline = gsap.timeline({ paused: true });
const modalContentTimeline = gsap.timeline({ paused: true });

// Set initial position
gsap.set(modalContentWrapper, {
  y: "100%",
  autoAlpha: 0,
});

modalTimeline.to(modal, {
  ease: "Power4.easeInOut",
  duration: 0.8,
  autoAlpha: 1,
});

modalContentTimeline.to(modalContentWrapper, {
  ease: "Power4.easeInOut",
  duration: 0.8,
  autoAlpha: 1,
  y: "0%",
});

// Common modal operations
const modalOperations = {
  show: () => {
    document.documentElement.classList.add("no-scroll");
    modalTimeline.play().timeScale(1);
    modal.scrollTop = 0;
  },

  hide: () => {
    modalTimeline.play().timeScale(-1);
    modalContentTimeline.play().timeScale(-1);
    document.documentElement.classList.remove("no-scroll");
  },

  setContent: (content) => {
    modalContentWrapper.innerHTML = content;
    modalContentTimeline.play();
  },
};

// get modal content
async function fetch_modal_content(id) {
  try {
    const response = await fetch(ajax_object.ajax_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        action: "get_modal_content",
        post_id: id,
        nonce: ajax_object.nonce,
      }),
    });

    const html = await response.text();
    modalOperations.setContent(`<div class="modal-content">${html}</div>`);

    // Initialize any Swiper sliders in the modal content
    const modalSwipers = modalContentWrapper.querySelectorAll(".swiper-afbeeldingen-slider");
    if (modalSwipers.length > 0) {
      initializeSwiper(modalSwipers, "swiper-afbeeldingen-slider");
    }
  } catch (error) {
    console.error("Error:", error);
    modalOperations.setContent('<div class="error">Error loading content</div>');
  }
}

// Switch the active search-modal tab/panel
function setSearchModalTab(name) {
  const tabs = modalContentWrapper.querySelectorAll(".search-modal__tab");
  const panels = modalContentWrapper.querySelectorAll(".search-modal__panel");

  tabs.forEach((tab) => {
    const isActive = tab.dataset.panel === name;
    tab.classList.toggle("is-active", isActive);
    tab.setAttribute("aria-selected", isActive ? "true" : "false");
  });

  panels.forEach((panel) => {
    const isActive = panel.dataset.panel === name;
    panel.classList.toggle("is-active", isActive);
    panel.hidden = !isActive;
  });

  // Focus the search input of the newly activated panel
  const input = modalContentWrapper.querySelector(`.search-modal__panel.is-active .search-modal__input`);
  if (input) input.focus();
}

// Live-filter the cards of a tentoonstellingen/pioniers tab via AJAX
async function filterSearchModal(postType, search) {
  const results = modalContentWrapper.querySelector(`[data-results="${postType}"]`);
  if (!results) return;

  results.classList.add("is-loading");

  try {
    const response = await fetch(ajax_object.ajax_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        action: "filter_search_modal",
        post_type: postType,
        search,
        nonce: ajax_object.nonce,
      }),
    });

    results.innerHTML = await response.text();
  } catch (error) {
    console.error("Error:", error);
  } finally {
    results.classList.remove("is-loading");
  }
}

// Initialize tabs and live filtering after the search modal is injected
function initSearchModal() {
  // Tab switching
  modalContentWrapper.querySelectorAll(".search-modal__tab").forEach((tab) => {
    tab.addEventListener("click", () => setSearchModalTab(tab.dataset.panel));
  });

  // Debounced live filtering
  modalContentWrapper.querySelectorAll(".search-modal__input[data-filter]").forEach((input) => {
    let timer;
    input.addEventListener("input", () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        filterSearchModal(input.dataset.filter, input.value.trim());
      }, 300);
    });
  });
}

// Modal handlers
const modalHandlers = {
  openSearchModal: () => {
    const template = document.getElementById("search-modal-template");
    if (!template) {
      console.error("Search modal template not found");
      return;
    }

    modalOperations.show();
    modalOperations.setContent(template.innerHTML);
    initSearchModal();

    // Focus the search input after modal opens
    setTimeout(() => {
      const searchInput = modalContentWrapper.querySelector(".search-modal__panel.is-active .search-modal__input");
      if (searchInput) searchInput.focus();
    }, 100);
  },

  openModal: (button) => {
    const postId = button.target.closest(".open-modal").dataset.id;
    if (!postId) {
      console.error("No post ID found");
      modalOperations.setContent('<div class="error">Error: No post ID found</div>');
      return;
    }

    modalOperations.show();
    fetch_modal_content(postId);
  },

  openImageInModal: (button) => {
    const imageUrl = button.target.closest(".open-image-in-modal").dataset.imageUrl;
    const imageCaption = button.target.closest(".open-image-in-modal").dataset.imageCaption;
    if (!imageUrl) {
      console.error("No image URL found in data-image-url attribute");
      return;
    }

    modalOperations.show();
    // Add image-modal class to wrapper for image-specific styling
    modalContentWrapper.classList.add("image-modal");
    modalOperations.setContent(
      `<figure><img src="${imageUrl}" alt="Modal image" class="modal-image"><p class="wp-caption-text modal-image-caption">${imageCaption}</p></figure>`
    );
  },

  openTextInModal: (button) => {
    const { text, title } = button.target.dataset;
    modalOperations.show();
    modalOperations.setContent(`
      <div class="modal-content">
        <div class="block--tab"></div>
        <h2 class="modal-title">${title}</h2>
        <div class="modal-text">${text}</div>
      </div>
    `);
  },

  openVideoInModal: (button) => {
    const videoElement = button.target.closest(".open-video-in-modal");
    const videoUrl = videoElement.dataset.videoUrl;
    const autoplayUrl = videoUrl + (videoUrl.includes("?") ? "&" : "?") + "autoplay=1&rel=0";

    modalOperations.show();
    modalOperations.setContent(`
      <div class="embed-container">
        <iframe 
          src="${autoplayUrl}" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
          allowfullscreen>
        </iframe>
      </div>
    `);
  },

  openImageTextInModal: (button) => {
    const imageElement = button.target;
    if (!imageElement) return;

    const { imageUrl, text, title, imageCaption, modalData } = imageElement.dataset;
    if (!imageUrl) return;

    let additionalContent = "";
    try {
      if (modalData) {
        const data = JSON.parse(modalData);
        additionalContent = `
          ${data.beschrijving ? `<p>${data.beschrijving}</p>` : ""}
          ${data.objectnummer ? `Objectnummer: ${data.objectnummer}</br>` : ""}
          ${data.collectie ? `Collectie: ${data.collectie}</br>` : ""}
          ${data.object_type ? `Object: ${data.object_type}</br>` : ""}
        `;
      }
    } catch (error) {
      console.error("Error parsing modal data:", error);
    }

    modalOperations.show();
    modalOperations.setContent(`
      <div class="modal-content">
        <div class="block--tab"></div>
        <div class="grid">
          <div class="col-12 col-md-4">
            <img src="${imageUrl}" alt="${title || ""}">
            ${imageCaption ? `<p class="wp-caption-text">${imageCaption}</p>` : ""}
          </div>
          <div class="col-12 col-md-8">
            ${title ? `<h2 class="modal-title">${title}</h2>` : ""}
            <div class="modal-text">
              ${text || ""}
              ${additionalContent}
            </div>
          </div>
        </div>
      </div>
    `);
  },
};

// Event handling
function initializeModalButtons() {
  const modalTypes = {
    ".open-modal": modalHandlers.openModal,
    ".open-image-in-modal": modalHandlers.openImageInModal,
    ".open-text-in-modal": modalHandlers.openTextInModal,
    ".open-video-in-modal": modalHandlers.openVideoInModal,
    ".open-image-text-in-modal": modalHandlers.openImageTextInModal,
  };

  // Add search button handler
  const searchButton = document.getElementById("open-search");
  if (searchButton) {
    searchButton.addEventListener("click", (e) => {
      e.preventDefault();
      modalHandlers.openSearchModal();
    });
  }

  document.addEventListener(
    "click",
    (e) => {
      for (const [selector, handler] of Object.entries(modalTypes)) {
        const target = e.target.closest(selector);
        if (target) {
          e.preventDefault();
          e.stopPropagation();
          handler({ target });
          break;
        }
      }
    },
    true
  );

  // Open a canon item's modal when arriving via /canon/?canon_id=<id>
  const canonId = new URLSearchParams(window.location.search).get("canon_id");
  if (canonId && /^\d+$/.test(canonId)) {
    const trigger = document.querySelector(`.open-modal[data-id="${canonId}"]`);
    if (trigger) {
      trigger.click();
    }
  }
}

// Close modal handlers
function closeModal() {
  const iframe = modalContentWrapper.querySelector("iframe");
  if (iframe) iframe.remove();

  // Add transition class just before closing for smooth image modal transitions
  if (modalContentWrapper.classList.contains("image-modal")) {
    modalContentWrapper.style.transition = "all 0.8s ease-in-out";
  }

  // Start the hide animation first
  modalOperations.hide();

  // Remove image-modal class after the animation completes to prevent flickering
  setTimeout(() => {
    modalContentWrapper.classList.remove("image-modal");
    modalContentWrapper.style.transition = ""; // Remove inline transition
  }, 800); // Match the modal closing animation duration (0.8s)
}

// Event listeners for closing
document.addEventListener("keyup", (e) => {
  if (e.code === "Escape") closeModal();
});

modal.addEventListener("click", (e) => {
  // Close modal when clicking on the background (not on content)
  if (e.target === modal) {
    closeModal();
  }
});

modalClose.addEventListener("click", closeModal);

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeModalButtons);
} else {
  initializeModalButtons();
}
